import uuidv4 from 'uuid/v4';
import { ACTIVITY_TYPES, OTHER_ACTIVITY_TYPES } from 'common/constants';

export const buildActivitySchema = (
  inputObj,
  activityType,
  uuid = uuidv4()
) => {
  let constructedId;
  if (activityType === 'FIELD') {
    constructedId = `ACTIVITY:FIELD:${inputObj.season}:${inputObj.fieldName}:${
      inputObj.fieldActivityType
    }:${uuid}`;
  }

  if (['STORAGE', 'DELIVERY'].includes(activityType)) {
    constructedId = `ACTIVITY:${activityType}:${inputObj.season}:${
      inputObj.source.id
    }:${uuid}`;
  }

  const optimisticObj = {
    id: constructedId,
    activityType,
    location: null,
    observations: null,
    harvesting: null,
    yield: null,
    fertilizing: null,
    chemicals: null,
    seeding: null,
    sprayingRate: null,
    createdAt: new Date(),
    ...inputObj,
  };

  let activityTypename;
  switch (activityType) {
    case 'FIELD':
      activityTypename = 'FieldActivity';
      break;

    case 'STORAGE':
      activityTypename = 'StorageActivity';
      break;

    case 'DELIVERY':
      activityTypename = 'DeliveryActivity';
      break;

    default:
      break;
  }
  optimisticObj.__typename = activityTypename;

  optimisticObj.activityDate =
    typeof optimisticObj.activityDate === 'object'
      ? optimisticObj.activityDate.toISOString()
      : optimisticObj.activityDate;

  // Typename for Fertilizing activities
  const { fertilizing } = optimisticObj;
  if (fertilizing && fertilizing.length > 0) {
    fertilizing.forEach(fertilizer => {
      fertilizer.__typename = 'Fertilizing';
      fertilizer.value.__typename = 'FertilizingValue';
    });
  }

  // Typename for Spraying activities
  const { chemicals } = optimisticObj;
  if (chemicals && chemicals.length > 0) {
    chemicals.forEach(chemical => {
      chemical.__typename = 'SprayingChemical';
      chemical.value.__typename = 'SprayingChemicalValue';
    });
  }

  // Typename for Scouting activities
  const { location, observations } = optimisticObj;
  if (location) {
    location.__typename = 'Coordinates';
  }

  if (observations && observations.length > 0) {
    observations.forEach(observation => {
      observation.__typename = 'Observation';
      observation.value.__typename = 'ObservationValue';
    });
  }

  // Typename for Harvesting activities
  const { harvesting, crop } = optimisticObj;
  if (harvesting && harvesting.length > 0) {
    harvesting.forEach(harvestObj => {
      harvestObj.__typename = 'HarvestingActivity';
      harvestObj.amount.__typename = 'Amount';
      harvestObj.destination.__typename = 'FieldDestination';
    });
  }

  // Typename for Seeding activities
  const { seeding } = optimisticObj;
  if (seeding) {
    seeding.__typename = 'Seeding';
    const { expectedYield, fertilizers, plantedArea } = seeding;
    const yieldEntry = expectedYield;
    if (expectedYield && !Array.isArray(expectedYield)) {
      seeding.expectedYield = [
        {
          timestamp: new Date().toISOString(),
          yield: {
            ...yieldEntry,
            __typename: 'Yield',
          },
          __typename: 'YieldDiary',
        },
      ];
    }
    if (fertilizers && fertilizers.length > 0) {
      fertilizers.forEach(fertilizer => {
        fertilizer.__typename = 'Fertilizing';
        fertilizer.value.__typename = 'FertilizingValue';
      });
    }

    if (plantedArea) {
      plantedArea.__typename = 'Area';
    }

    // set undefined property to null
    Object.keys(seeding).forEach(key => {
      if (seeding[key] === undefined) {
        seeding[key] = null;
      }
    });
  }

  if (crop) {
    crop.__typename = 'CropIdentifier';
  }

  // Typename for Storage activities
  const { destinations, source } = optimisticObj;

  if (destinations && destinations.length > 0) {
    if (activityType === 'STORAGE') {
      destinations.forEach(dest => {
        dest.amount.__typename = 'Amount';
        dest.destination.__typename = 'StorageActivityDestination';
        dest.__typename = 'StorageActivityDestinationAmount';
      });
    }
  }

  if (source) {
    let typename;
    switch (activityType) {
      case 'STORAGE':
        typename = 'StorageActivitySource';
        break;
      case 'DELIVERY':
        typename = 'DeliveryActivitySource';
        break;
      default:
        break;
    }
    source.__typename = typename;
  }

  // yield is a reserved world, we can't destruct yield from optimisticObj here
  if (optimisticObj.yield) {
    optimisticObj.yield.__typename = 'Yield';
  }

  // Typename for Delivery activities
  const { destination } = optimisticObj;
  if (destination && activityType === 'DELIVERY') {
    destination.amount.__typename = 'Amount';
    destination.destination.__typename = 'DeliveryActivityDestination';
    destination.__typename = 'DeliveryActivityDestinationAmount';
  }

  return optimisticObj;
};

export const buildCropchema = payload => {
  const { crop, expectedYield, plantedArea } = payload;
  payload.__typename = 'FieldCrop';
  if (crop) {
    crop.__typename = 'CropIdentifier';
  }

  if (expectedYield && Array.isArray(expectedYield)) {
    for (const index in expectedYield) {
      expectedYield[index].__typename = 'YieldDiary';
      expectedYield[index].yield.harvested = false;
      expectedYield[index].yield.__typename = 'Yield';
    }
  }

  if (plantedArea) {
    plantedArea.__typename = 'Area';
  }

  return payload;
};

export const mapInitialValuesForStorageActivity = activity => {
  const {
    activityDate,
    crop,
    source,
    destinations,
    notes,
    finishDate,
  } = activity;

  const [amount, storages_amount, storages] = destinations.reduce(
    (acc, cur) => {
      const [accTtlAmt, accAmts, accStg] = acc;
      const { amount, destination } = cur;
      const newTtlAmt = accTtlAmt + amount.size;
      return [
        newTtlAmt,
        [...accAmts, amount.size],
        [...accStg, JSON.stringify(destination)],
      ];
    },
    [0, [], []]
  );

  // return { activityDate, crop, storage: source, notes };
  const result = {
    activityDate,
    crop: JSON.stringify(crop),
    storage: JSON.stringify(source),
    storages,
    storages_amount,
    amount,
    notes,
    finishDate,
    status: !!finishDate,
  };

  return result;
};

export const mapInitialValuesForDeliveryActivity = activity => {
  const {
    activityDate,
    crop,
    destination,
    finishDate,
    notes,
    source,
  } = activity;

  const { size } = destination.amount;

  return {
    activityDate,
    crop: JSON.stringify(crop),
    storage: JSON.stringify(source),
    amount: size,
    contracts: [JSON.stringify(destination.destination)],
    contracts_amount: [size],
    notes,
    finishDate,
    status: !!finishDate,
  };
};

export const mapInitialValues = (
  {
    activityDate,
    finishDate,
    crop,
    fieldName,
    chemicals,
    sprayingRate,
    fieldActivityType,
    location,
    observations,
    fertilizing,
    harvesting,
    seeding,
    notes,
    ...rest
  },
  { fields }
) => {
  const commonFields = {
    activityDate: new Date(activityDate),
    status: !!finishDate,
    crop: JSON.stringify(crop),
    fieldName: JSON.stringify({ name: fieldName }),
    notes,
  };

  if (finishDate) {
    commonFields.finishDate = finishDate;
  }

  switch (fieldActivityType) {
    case ACTIVITY_TYPES.SPRAYING:
      return {
        ...commonFields,
        chemicals:
          chemicals &&
          chemicals.map(chemical =>
            JSON.stringify({
              __typename: 'Chemical',
              id: `CHEMICAL:${chemical.name}`,
              name: chemical.name,
              brand: chemical.value.brand,
              type: chemical.value.type,
            })
          ),
        chemicals_amount:
          chemicals && chemicals.map(chemical => chemical.value.notes),
        sprayingRate,
      };
    case ACTIVITY_TYPES.FERTILIZING:
      return {
        ...commonFields,
        fertilizers_amount:
          fertilizing && fertilizing.map(fertilizer => fertilizer.value.notes),
        fertilizers:
          fertilizing &&
          fertilizing.map(fertilizer => {
            const { notes, __typename, ...rest } = fertilizer.value; // eslint-disable-line no-unused-vars
            return JSON.stringify({
              __typename: 'Fertilizer',
              id: `FERTILIZER:${fertilizer.name}`,
              name: fertilizer.name,
              ...rest,
            });
          }),
      };
    case ACTIVITY_TYPES.SCOUTING:
      return {
        ...commonFields,
        location: `${location.lat}, ${location.lng}`,
        insects: observations[1].value.severity,
        insectsNotes: observations[1].value.notes,
        weeds: observations[0].value.severity,
        weedsNotes: observations[0].value.notes,
      };
    case ACTIVITY_TYPES.HARVESTING: {
      const [amount, storages_amount, storages] = harvesting.reduce(
        (acc, cur) => {
          const [accTtlAmt, accAmts, accStg] = acc;
          const { amount, destination } = cur;
          const newTtlAmt = accTtlAmt + amount.size;
          return [
            newTtlAmt,
            [...accAmts, amount.size],
            [...accStg, JSON.stringify(destination)],
          ];
        },
        [0, [], []]
      );
      let formattedFieldName = commonFields.fieldName;
      const fieldObj = fields.find(f => f.name === fieldName);
      if (fieldObj && fieldObj.size) {
        const [harvestingItem] = harvesting;
        const {
          amount: { size },
        } = harvestingItem;
        formattedFieldName = JSON.stringify({ name: fieldName, size });
      }
      return {
        ...commonFields,
        fieldName: formattedFieldName,
        amount,
        storages,
        storages_amount,
        yield: rest.yield ? `${rest.yield.size}` : '0',
      };
    }
    case ACTIVITY_TYPES.SEEDING: {
      const {
        plantedArea,
        seedingRate,
        expectedYield,
        inoculant,
        chemical,
        fertilizers,
      } = seeding;
      return {
        ...commonFields,
        crop: JSON.stringify({ ...crop, variety: null }),
        variety: crop.variety,
        plantedArea: String(plantedArea.size),
        seedingRate,
        expectedYield: expectedYield[0].yield.size,
        inoculant,
        chemical,
        fertilizers:
          fertilizers &&
          fertilizers.map(fertilizer => {
            const { notes, __typename, ...rest } = fertilizer.value; // eslint-disable-line no-unused-vars
            return JSON.stringify({
              __typename: 'Fertilizer',
              id: `FERTILIZER:${fertilizer.name}`,
              name: fertilizer.name,
              ...rest,
            });
          }),
        fertilizers_amount:
          fertilizers && fertilizers.map(fertilizer => fertilizer.value.notes),
      };
    }
    case OTHER_ACTIVITY_TYPES.TILLING:
    case OTHER_ACTIVITY_TYPES.IRRIGATION:
    case OTHER_ACTIVITY_TYPES.CUSTOM:
      return {
        ...commonFields,
        type: fieldActivityType,
      };
    default:
      return commonFields;
  }
};

export const formatFieldActivityUpdateInput = ({
  fieldName, // eslint-disable-line no-unused-vars
  season, // eslint-disable-line no-unused-vars
  fieldActivityType, // eslint-disable-line no-unused-vars
  crop, // eslint-disable-line no-unused-vars
  ...rest
}) => rest;



// WEBPACK FOOTER //
// ./src/containers/ActivityForm/utils.js